import {useEffect, useRef, useState} from "react";
import {AcquisitionProperty} from "../types";
import {useGetAcquisitionPropertyDetailsWhileJobRunningLazyQuery} from "../../../__generated__/generated_types";
import {isJobRunning} from "../../admin/jobs/helpers";

export interface IUseFetchUpdatesForJobs {
    addForUpdate: (jobId: string, propertyId: string) => void;
    updates: Map<string, AcquisitionProperty>;
}

export function useFetchUpdatesForJobs(): IUseFetchUpdatesForJobs {

    // This is a map of job id -> property id.
    // A job at this point only applies to one property.
    const map = useRef<Map<string, string>>(new Map());
    const interval = useRef<NodeJS.Timeout | null>(null);

    const [updates, setUpdates] = useState<Map<string, AcquisitionProperty>>(new Map());
    const [fetchUpdates, {data, loading}] = useGetAcquisitionPropertyDetailsWhileJobRunningLazyQuery({
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
        return () => {
            stopInterval();
            map.current = new Map();
        };
    }, []);

    useEffect(() => {
        if(loading || !data) {
            return;
        }

        for(const job of data.getJobs) {
            if(!isJobRunning(job)) {
                map.current.delete(job.id);
            }
        }

        if(map.current.size === 0) {
            stopInterval();
        }

        setUpdates(new Map(data.getAcquisitionProperties.map(p => [p.id, p])));
    }, [data, loading]);

    function stopInterval(): void {
        if(interval.current) {
            clearInterval(interval.current);
            interval.current = null;
        }
    }

    function startInterval(): void {
        interval.current = setInterval(() => {
            const jobIds: string[] = [];
            const propertyIds = new Set<string>();

            map.current.forEach((value, key) => {
                jobIds.push(key);
                propertyIds.add(value);
            });

            fetchUpdates({
                variables: {
                    jobIds: jobIds,
                    propertyIds: Array.from(propertyIds)
                }
            });
        }, 30000);
    }

    const addForUpdate = (jobId: string, propertyId: string) => {
        map.current.set(jobId, propertyId);
        if(interval.current === null) {
            startInterval();
        }
    };

    return {
        addForUpdate: addForUpdate,
        updates: updates
    };
}